import { Box, Stack } from "@mui/material";
import ManagementUpdateHeader from "../ManagementsUpdateHeader/ManagementsUpdateHeader";
import { useTranslation } from "react-i18next";
import CupsCifSelector from "../../CupsCifSelector/CupsCifSelector";
import {
  SubmitUpdateContactData,
  UpdateContactDataRequestBody,
} from "../../../core/models/managementsUpdate/managementsUpdateContact/managementsUpdateContact.model";
import { useContext, useEffect, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import theme from "../../../muiTheme";
import "./ManagementsUpdateContact.scss";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import ManagementsUpdateNewContact from "./ManagementsUpdateNewContact/ManagementsUpdateNewContact";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import { updateContactData } from "../../../core/services/managementsUpdate/managementsUpdate.service";
import {
  ManagementCupsInfo,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  formatOwnerDataCup,
  getManagementsRequestTypeData,
} from "../../../utils/components/managements/managements.util";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_RESOLVED,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
} from "../../../core/constants/constants";
import { useSearchParams } from "react-router-dom";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";

const ManagementUpdateContact = () => {
  const { t } = useTranslation();
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);
  const [searchParms] = useSearchParams();

  const [managementCupsInfo, setManagementCupsInfo] = useState<
    ManagementCupsInfo[] | undefined
  >(undefined);

  const idCups = searchParms.get("idCups") || "";
  const [cupsSelected, setCupsSelected] = useState<ManagementUpdateCup[]>([]);
  const [step, setStep] = useState<number>(idCups.length ? 0 : -1);

  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
    });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ownerDataCup =
    ownerData?.cups.find(
      ({ idCups: ownerDataIdCups }) => ownerDataIdCups.toString() === idCups
    ) || initialOwnerDataCup;

  useEffect(() => {
    if (idCups.length && ownerData) {
      setCupsSelected([formatOwnerDataCup(ownerDataCup)]);
    }
  }, [idCups, ownerData, ownerDataCup]);

  const handleSubmit: SubmitUpdateContactData = (contacts) => {
    if (ownerData && userData) {
      const cupContracts = contacts
        .filter(({ isUpdateContact }) => isUpdateContact)
        .map(({ cupsContractId, email, phone }) => ({
          cupsContractId,
          emails: email
            .filter(({ value }) => value.length)
            .map(({ value }) => value),
          telefonos: phone
            .filter(({ value }) => value.length)
            .map(({ value }) => value),
        }));

      setIsLoading(true);
      setStep(1);

      const updateContactDataRequestBody: UpdateContactDataRequestBody[] =
        cupContracts.map(({ cupsContractId, emails, telefonos }) => ({
          IdContrato: cupsContractId.toString(),
          Emails: emails,
          Telefonos: telefonos,
        }));

      updateContactData(updateContactDataRequestBody, userData.token)
        .then((response) => {
          const cupsInfo: ManagementCupsInfo[] = response.map((item) => {
            const address = ownerData?.cups.find(
              (cups) => cups.codeCups === item.Cups
            )?.address;
            return {
              cupsCode: item.Cups,
              address: address ?? "",
              environment: item.EntornoBD,
            };
          });
          setRequestState(MANAGEMENTS_STATE_RESOLVED);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
              MANAGEMENTS_STATE_RESOLVED,
              isAgent ?? false
            )
          );
          setManagementCupsInfo(cupsInfo);

          setStep(3);
          setIsLoading(false);
        })
        .catch(() => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false
            )
          );
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Box component="section" className="management-update-contact">
      <ManagementUpdateHeader
        title={"Actualizar datos de contacto"}
        step={step}
        cupsSelected={cupsSelected}
        showStepper={false}
        ownerDataCup={ownerDataCup}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={5}
        paddingBottom={10}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        {step === -1 && (
          <CupsCifSelector
            withContainer
            setUpdateCups={(selectedCups) => {
              setCupsSelected(selectedCups);
              setStep(0);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
            selectedCups={cupsSelected}
            setSelectedCups={setCupsSelected}
            title={"Gestiones.¿Dónde quieres aplicar el cambio?"}
            description={"Gestiones.El cambio se solicitará"}
          />
        )}
        {step === 0 && cupsSelected.length && (
          <ManagementsUpdateNewContact
            handleSubmit={handleSubmit}
            cupsSelected={cupsSelected}
          />
        )}
        {[1, 2].includes(step) && (
          <ManagementsRequestState
            state={requestState}
            isLoading={isLoading}
            title={t(requestStateData?.title)}
            description={t(requestStateData?.description)}
          />
        )}

        {[3].includes(step) &&
          managementCupsInfo?.map((request) => (
            <ManagementsRequestState
              state={requestState}
              isLoading={isLoading}
              title={t(requestStateData?.title)}
              description={t(requestStateData?.description)}
              cupsInfo={request}
            />
          ))}
      </Stack>
    </Box>
  );
};

export default ManagementUpdateContact;
